/**
 * Labstep
 *
 * @module grid/SmartTableProtocolCondition/coldefs/protocol-value
 * @desc Grid SmartTable Column Definitions
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { ICONS } from 'labstep-web/constants/icons';
import { DataGrid } from 'labstep-web/core/DataGrid';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import { TextLink } from 'labstep-web/core/DataGrid/Text/Link';
import type {
  CellEditorParams,
  CellRendererParams,
  HeaderComponentParamsProps,
} from 'labstep-web/core/DataGrid/types';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/types';
import { getProtocolConditionResourceProps } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/utils';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolCondition/components/ColumnHeader';
import { GridSmartTableProtocolConditionService } from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition.service';
import type { Experiment } from 'labstep-web/models/experiment.model';
import type { Protocol } from 'labstep-web/models/protocol.model';
import type { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { Resource } from 'labstep-web/models/resource.model';
import { ResourceShowCenterSecondaryInfo } from 'labstep-web/screens/Resource/Show/Center/SecondaryInfo';
export const getProtocolValueResourceColumnDef = (
  experimentOrProtocol: Experiment | Protocol,
  name: string,
): SmartTableColDef => ({
  colId: `protocol_value:${name}:resource`,
  sortable: true,
  headerName: 'Resource',
  headerComponent: GridSmartTableColumnHeader,
  headerComponentParams: (params: HeaderComponentParamsProps) => {
    const constant =
      GridSmartTableProtocolConditionService.getProtocolValueConstant(
        experimentOrProtocol,
        name,
      );
    return {
      toolPanelColumnName: `Resource - ${name}`,
      icon: ICONS.resource.primary,
      secondaryHeader: name,
      isConstant: !!constant,
      experimentOrProtocol,
      protocolValue:
        GridSmartTableProtocolConditionService.getProtocolValue(
          experimentOrProtocol,
          name,
        ),
    };
  },
  editable: (params: EditableCallbackParams) =>
    GridSmartTableProtocolConditionService.getProtocolValueEditable(
      params,
      experimentOrProtocol,
      name,
    ),
  valueGetter: (params: ValueGetterParams) => {
    if (!params.node) {
      return '';
    }

    const protocolValueConstant =
      GridSmartTableProtocolConditionService.getProtocolValueConstant(
        experimentOrProtocol,
        name,
      );

    const protocolValue =
      GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
        params.node.data,
        experimentOrProtocol,
        name,
      ) || protocolValueConstant;

    const resourceItem = protocolValue?.is_input
      ? protocolValue?.resourceItem
      : protocolValue?.resource_item_output;

    const variableTemplate =
      GridSmartTableProtocolConditionService.getProtocolValueVariableTemplate(
        experimentOrProtocol,
        name,
      );

    if (!variableTemplate && !protocolValueConstant) {
      return '';
    }

    let resource = variableTemplate?.resource;

    if (resourceItem) {
      resource = resourceItem.resource;
    }

    if (protocolValue?.resource) {
      resource = protocolValue.resource;
    }

    if (!resource) {
      return '';
    }

    return resource.guid;
  },
  cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
    const variableTemplate =
      GridSmartTableProtocolConditionService.getProtocolValueVariableTemplate(
        experimentOrProtocol,
        name,
      );

    const protocolValueConstant =
      GridSmartTableProtocolConditionService.getProtocolValueConstant(
        experimentOrProtocol,
        name,
      );

    const protocolValue =
      GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
        params.node.data,
        experimentOrProtocol,
        name,
      ) || protocolValueConstant;

    if (!variableTemplate && !protocolValueConstant) {
      return null;
    }

    const resourceItem = protocolValue?.is_input
      ? protocolValue?.resourceItem
      : protocolValue?.resource_item_output;

    let resource = variableTemplate?.resource;

    if (resourceItem) {
      resource = resourceItem.resource;
    }

    if (protocolValue?.resource) {
      resource = protocolValue.resource;
    }

    if (!resource) {
      return (
        <span>
          <DataGridPlaceholder
            children={'Select'}
            params={params}
            editable={GridSmartTableProtocolConditionService.getProtocolValueEditable(
              params,
              experimentOrProtocol,
              name,
            )}
          />
        </span>
      );
    }

    return (
      <div>
        <TextLink
          entity={resource}
          entityPreviewProps={{
            secondaryInfo: (
              <ResourceShowCenterSecondaryInfo resource={resource} />
            ),
          }}
          disabled
        />
      </div>
    );
  },
  cellEditor: DataGrid?.CellEditor?.SearchSelectModal,
  cellEditorParams: (params: CellEditorParams<ProtocolCondition>) => {
    if (!params.node) {
      return false;
    }

    const protocolValueConstant =
      GridSmartTableProtocolConditionService.getProtocolValueConstant(
        experimentOrProtocol,
        name,
      );

    const protocolValue =
      GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
        params.node.data,
        experimentOrProtocol,
        name,
      ) || protocolValueConstant;

    const variableTemplate =
      GridSmartTableProtocolConditionService.getProtocolValueVariableTemplate(
        experimentOrProtocol,
        name,
      );

    if (!variableTemplate && !protocolValueConstant) {
      return '';
    }

    const props = getProtocolConditionResourceProps(
      params.node.data,
      variableTemplate!,
    );
    if (variableTemplate?.resource_template) {
      props.searchProps = {
        params: {
          template_id: variableTemplate!.resource_template.guid,
        },
      };
    }

    props.childEntityName = Resource.entityName;

    props.onChange = (option): void => {
      if (protocolValue) {
        GridSmartTableProtocolConditionService.updateEntity(
          params,
          protocolValue.entityName,
          protocolValue.idAttr,
          {
            resource_id: option.id,
          },
        );
      } else {
        GridSmartTableProtocolConditionService.createEntity(
          params,
          ProtocolValue.entityName,
          {
            variable_template_guid: variableTemplate!.guid,
            resource_id: option.id,
            protocol_condition_id: params.data.guid,
            [`${experimentOrProtocol.entityName}_id`]:
              experimentOrProtocol.guid,
          },
        );
      }
    };

    props.preventDefaultOnChange = true;
    return {
      props,
    };
  },
  valueSetter: (params) => {
    const newValue = params.newValue;
    if (
      !params.node ||
      newValue === '' ||
      params.newValue === params.oldValue
    ) {
      return false;
    }

    const protocolValueConstant =
      GridSmartTableProtocolConditionService.getProtocolValueConstant(
        experimentOrProtocol,
        name,
      );

    const protocolValue =
      GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
        params.node.data,
        experimentOrProtocol,
        name,
      ) || protocolValueConstant;

    const variableTemplate =
      GridSmartTableProtocolConditionService.getProtocolValueVariableTemplate(
        experimentOrProtocol,
        name,
      );

    if (!variableTemplate && !protocolValueConstant) {
      return false;
    }

    const props = getProtocolConditionResourceProps(
      params.node.data,
      variableTemplate!,
    );
    if (variableTemplate?.resource_template) {
      props.searchProps = {
        params: {
          template_id: variableTemplate!.resource_template.guid,
        },
      };
    }

    props.childEntityName = Resource.entityName;

    if (protocolValue) {
      return GridSmartTableProtocolConditionService.updateEntity(
        params,
        protocolValue.entityName,
        protocolValue.idAttr,
        {
          resource_id: newValue,
        },
      );
    } else {
      return GridSmartTableProtocolConditionService.createEntity(
        params,
        ProtocolValue.entityName,
        {
          variable_template_guid: variableTemplate!.guid,
          resource_id: newValue,
          protocol_condition_id: params.data.guid,
          [`${experimentOrProtocol.entityName}_id`]:
            experimentOrProtocol.guid,
        },
      );
    }
  },
  cellStyle: (params) =>
    GridSmartTableProtocolConditionService.getCellStyle(
      params,
      experimentOrProtocol,
      ProtocolValue.entityName,
      name,
    ),
  valueClearer: () => {
    return null;
  },
  suppressKeyboardEvent: suppressKeyboardEventClearCells,
});
